import { useState, useRef, useEffect } from "react";

interface Message {
  id: number;
  text: string;
  from: "bot" | "user";
  time: string;
}

const botResponses: Record<string, string> = {
  bonjour: "Bonjour ! Je suis l'assistant de l'ASC Sonatel. Comment puis-je vous aider aujourd'hui ?",
  salut: "Salut ! Bienvenue sur l'application ASC Sonatel. Que puis-je faire pour vous ?",
  club: "L'ASC Sonatel est l'Association Sportive et Culturelle de la SONATEL. Nous proposons des activités sportives (football, basketball, tennis de table, volleyball, natation) et culturelles pour tous les salariés et leurs familles.",
  inscription: "Pour vous inscrire à l'ASC Sonatel, rendez-vous dans la section Formulaires ou contactez-nous à ASCSonatel2@orange-sonatel.com. Les inscriptions sont ouvertes toute l'année !",
  événement: "Consultez notre calendrier sportif dans la section Sport > Calendrier pour voir tous les prochains événements, matchs et tournois.",
  boutique: "Notre boutique propose des goodies ASC Sonatel : maillots, casquettes, sacs et bien plus. Visitez la section Boutique pour découvrir notre catalogue.",
  squad: "Les squads sont des groupes de 4 membres maximum. Vous pouvez rejoindre un squad existant ou en créer un nouveau dans la section Communauté > Squads.",
  contact: "Contactez-nous : ASCSonatel2@orange-sonatel.com | 77 892 82 82 | Siège SONATEL, Rue Wagane Diouf, Dakar",
  horaire: "Nos entraînements ont lieu du lundi au samedi. Consultez la page Le Club > Infos Pratiques pour les horaires détaillés par sport.",
  default: "Je n'ai pas bien compris votre question. Vous pouvez me demander des informations sur le club, les inscriptions, les événements, la boutique, les squads ou les horaires.",
};

function getBotResponse(input: string): string {
  const lower = input.toLowerCase();
  for (const key of Object.keys(botResponses)) {
    if (lower.includes(key)) return botResponses[key];
  }
  return botResponses.default;
}

function getTime() {
  return new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Bonjour ! Je suis l'assistant ASC Sonatel. Posez-moi vos questions sur le club, les événements, la boutique ou les inscriptions !",
      from: "bot",
      time: getTime(),
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendMessageText = (messageText: string) => {
    const normalizedText = messageText.trim();
    if (!normalizedText) return;

    const userMsg: Message = { id: Date.now(), text: normalizedText, from: "user", time: getTime() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      const botMsg: Message = {
        id: Date.now() + 1,
        text: getBotResponse(normalizedText),
        from: "bot",
        time: getTime(),
      };
      setMessages((prev) => [...prev, botMsg]);
      setTyping(false);
    }, 900);
  };

  const sendMessage = () => {
    sendMessageText(input);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") sendMessage();
  };

  const quickReplies = ["Inscription", "Événements", "Boutique", "Squads", "Horaires"];

  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-50">
      {/* Chat Window */}
      {open && (
        <div
          className="mb-4 w-[300px] sm:w-[360px] bg-white/85 sm:bg-white rounded-3xl shadow-2xl border border-white/30 sm:border-gray-100 overflow-hidden flex flex-col backdrop-blur-md"
          style={{ height: "420px" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#E8521A] to-[#d44a16] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-robot-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">Assistant ASC</div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  <span className="text-white/80 text-xs">En ligne</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line text-white"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF9F7]/80 sm:bg-[#FAF9F7]">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${msg.from === "user" ? "" : "flex items-end gap-2"}`}>
                  {msg.from === "bot" && (
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#E8521A] flex-shrink-0 mb-1">
                      <i className="ri-robot-line text-white text-xs"></i>
                    </div>
                  )}
                  <div>
                    <div
                      className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.from === "user"
                          ? "bg-[#E8521A] text-white rounded-br-sm"
                          : "bg-white text-[#2D2D2D] rounded-bl-sm border border-gray-100"
                      }`}
                    >
                      {msg.text}
                    </div>
                    <div className={`text-xs text-gray-400 mt-1 ${msg.from === "user" ? "text-right" : "text-left"}`}>
                      {msg.time}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#E8521A] flex-shrink-0">
                  <i className="ri-robot-line text-white text-xs"></i>
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm border border-gray-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          <div className="px-4 py-2 bg-white/90 sm:bg-white border-t border-white/40 sm:border-gray-100 flex gap-2 overflow-x-auto">
            {quickReplies.map((r) => (
              <button
                key={r}
                onClick={() => sendMessageText(r)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full border border-[#E8521A] text-[#E8521A] text-xs font-medium hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {r}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white/90 sm:bg-white border-t border-white/40 sm:border-gray-100 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Posez votre question..."
              className="flex-1 bg-[#FAF9F7] rounded-full px-4 py-2.5 text-sm text-[#2D2D2D] outline-none border border-gray-200 focus:border-[#E8521A] transition-colors"
            />
            <button
              onClick={sendMessage}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#E8521A] hover:bg-[#d44a16] transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-fill text-white text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#E8521A] to-[#d44a16] shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer relative"
          style={{ boxShadow: "0 8px 32px rgba(232, 82, 26, 0.4)" }}
        >
          <i className="ri-message-3-line text-white text-lg sm:text-2xl transition-all duration-300"></i>
        </button>
      )}
    </div>
  );
}
